import { FC, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import useBenguide from 'hooks/benguide';
import { useAppSelector } from 'hooks/redux';
import EditableHotspot from 'components/EditableHotspot/EditableHotspot';
import ContentPreviewer from 'components/ContentPreviewer/ContentPreviewer';
import Spinner from 'components/Spinner/Spinner';
import RemoveCustomSection from 'modules/benefitsWellness/components/RemoveCustomComponent/RemoveCustomSection';
import iconEdit from 'images/icon-edit.svg';
import iconDelete from 'images/icons/remove-icon.svg';
import AddYourText from 'modules/benefitsWellness/components/AddYourText/AddYourText';
import TextEditorModal from 'modules/customPages/components/TextEditor/TextEditor';
import SectionMoveControls from 'modules/customPages/components/SectionMoveControls/SectionMoveControls';

import styles from './textComponent.module.less';

interface TextComponentProps {
  id: string;
  content?: string;
  customPageId: string;
  onSave?: (id: string, content: string) => void;
  onDelete?: (id: string) => void;
  moveProps?: {
    onMoveUp: () => void;
    onMoveDown: () => void;
    hideUp: boolean;
    hideDown: boolean;
    showControls: boolean;
  };
}

const TextComponent: FC<TextComponentProps> = ({
  id,
  content = '',
  customPageId,
  onSave,
  onDelete,
  moveProps,
}) => {
  const { masterId, isEditMode } = useBenguide();
  const [isLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const richMediaContent = useAppSelector(
    (state) => state.customPages.richMediaContent
  );
  const key = `${customPageId}-${id}`;
  const reduxContent = richMediaContent[key]?.content;

  const displayContent = reduxContent !== undefined ? reduxContent : content;
  const showAddContentBtn = isEmpty(displayContent) && isEditMode;

  const handleSave = (newContent: string) => {
    if (onSave) {
      onSave(id, newContent);
    }
    setIsModalOpen(false);
  };

  const handleDelete = useCallback(() => {
    setShowDeleteConfirmation(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (onDelete) {
      onDelete(id);
    }
    setShowDeleteConfirmation(false);
  }, [id, onDelete]);

  return (
    <div
      className={`${styles.textComponentWrapper} ${
        !isEditMode || !isEmpty(displayContent) ? styles.previewMode : ''
      }`}
    >
      {moveProps && (
        <SectionMoveControls
          onMoveUp={moveProps.onMoveUp}
          onMoveDown={moveProps.onMoveDown}
          hideUp={moveProps.hideUp}
          hideDown={moveProps.hideDown}
          showControls={moveProps.showControls}
        />
      )}
      <div className={styles.textComponentContainer}>
        <div className={styles.textContentArea}>
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              {showAddContentBtn || !displayContent ? (
                <EditableHotspot
                  editText="Edit"
                  alwaysVisible={false}
                  editIcon={<img src={iconEdit} alt="edit" />}
                  deleteText="Remove"
                  deleteIcon={<img src={iconDelete} alt="delete" />}
                  onDelete={handleDelete}
                  showDeleteButton={isEditMode}
                  customModal={(
                    visible: boolean,
                    onClose: () => void,
                    editorKey: string
                  ) => (
                    <TextEditorModal
                      isOpenModal={visible}
                      closeModal={onClose}
                      content={displayContent}
                      benguideId={masterId}
                      onSave={handleSave}
                      editorKey={editorKey}
                      customPageId={customPageId}
                      customPageSectionId={id}
                      componentType="rich_media"
                    />
                  )}
                >
                  <div style={{ padding: '20px' }}>
                    <div
                      onClick={() => setIsModalOpen(true)}
                      className={styles.addTextButton}
                    >
                      <AddYourText />
                    </div>
                    <div className={styles.textPlaceholder}>
                      <div className={styles.textPlaceholderBar}></div>
                      <div className={styles.textPlaceholderBar}></div>
                      <div className={styles.textPlaceholderBar}></div>
                      <div className={styles.textPlaceholderBar}></div>
                      <div className={styles.textPlaceholderBar}></div>
                    </div>
                  </div>
                </EditableHotspot>
              ) : (
                <EditableHotspot
                  editText="Edit"
                  alwaysVisible={showAddContentBtn}
                  editIcon={<img src={iconEdit} alt="edit" />}
                  deleteText="Remove"
                  deleteIcon={<img src={iconDelete} alt="delete" />}
                  onDelete={handleDelete}
                  showDeleteButton={!isEmpty(displayContent) && isEditMode}
                  customModal={(
                    visible: boolean,
                    onClose: () => void,
                    editorKey: string
                  ) => (
                    <TextEditorModal
                      isOpenModal={visible}
                      closeModal={onClose}
                      content={displayContent}
                      benguideId={masterId}
                      onSave={handleSave}
                      editorKey={editorKey}
                      customPageId={customPageId}
                      customPageSectionId={id}
                      componentType="rich_media"
                    />
                  )}
                >
                  <div className={styles.textContentPreview}>
                    {displayContent ? (
                      <ContentPreviewer htmlContent={displayContent} />
                    ) : (
                      <div className={styles.textPlaceholder}>
                        <div className={styles.textPlaceholderBar}></div>
                        <div className={styles.textPlaceholderBar}></div>
                        <div className={styles.textPlaceholderBar}></div>
                        <div className={styles.textPlaceholderBar}></div>
                        <div className={styles.textPlaceholderBar}></div>
                      </div>
                    )}
                  </div>
                </EditableHotspot>
              )}
            </>
          )}

          {/* Modal for AddYourText click */}
          {showAddContentBtn && (
            <TextEditorModal
              isOpenModal={isModalOpen}
              closeModal={() => setIsModalOpen(false)}
              content={displayContent}
              benguideId={masterId}
              onSave={handleSave}
              editorKey={`text-add-${id}`}
              customPageId={customPageId}
              customPageSectionId={id}
              componentType="rich_media"
            />
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <RemoveCustomSection
        visible={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onRemove={confirmDelete}
      />
    </div>
  );
};

TextComponent.propTypes = {
  id: PropTypes.string.isRequired,
  content: PropTypes.string,
  customPageId: PropTypes.string.isRequired,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  moveProps: PropTypes.shape({
    onMoveUp: PropTypes.func.isRequired,
    onMoveDown: PropTypes.func.isRequired,
    hideUp: PropTypes.bool.isRequired,
    hideDown: PropTypes.bool.isRequired,
    showControls: PropTypes.bool.isRequired,
  }),
};

TextComponent.defaultProps = {
  content: '',
  onSave: undefined,
};

export default TextComponent;
